import React from 'react';
import PropTypes from 'prop-types';
import Add from '@material-ui/icons/Add';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import CircularProgress from 'components/shared/CircularProgress';
import {
  Button,
  Dialog,
  Typography,
  makeStyles,
  Box,
} from '@material-ui/core';
import PATHS from 'constants/paths';
import HideRemove from './HideRemove';
import CottagesListItem from './CottagesListItem';

const styles = makeStyles((theme) => ({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
  },
  button: {
    alignSelf: 'center',
    marginBottom: theme.spacing(2),
  },
  inProgress: {
    marginBottom: theme.spacing(2),
  },
  title: {
    marginLeft: theme.spacing(3),
  },
  soft: {
    padding: theme.spacing(2),
  },
}));

const Cottages = ({
  onActivate,
  onRemove,
  cottages,
  isCanUse,
  isLoading,
  isStrict,
  updatingDialogs,
}) => {
  const classes = styles();
  const timer = React.useRef(null);
  const [isHideRemoveOpen, setIsHideRemoveOpen] = React.useState(false);
  const [cottageId, setCottageId] = React.useState(null);
  const [notReviewed, setNotReviewed] = React.useState([]);
  const [reviewed, setReviewed] = React.useState([]);

  React.useEffect(() => {
    const _notReviewed = cottages ? cottages.filter(({ reviewed_at }) => !reviewed_at) : [];
    const _reviewed = cottages ? cottages.filter(({ reviewed_at }) => reviewed_at) : [];

    setNotReviewed(_notReviewed.sort((a, b) => Number(!b.asked_for_review_at) - Number(!a.asked_for_review_at)));
    setReviewed(
      _reviewed
        .sort((a, b) => Number(!b.paid_at) - Number(!a.paid_at))
        .sort((a, b) => Number(!a.hidden_at) - Number(!b.hidden_at)),
    );
  }, [cottages]);

  React.useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  const onActivateClick = (id) => onActivate(id, true);

  const closeHideRemove = () => setIsHideRemoveOpen(false);

  const onHide = () => {
    closeHideRemove();

    onActivate(cottageId, false);
  };

  const onRemoveClick = () => {
    onRemove(cottageId);

    timer.current = setTimeout(() => setIsHideRemoveOpen(false), 100);
  };

  const openHideRemove = (id) => {
    setCottageId(id);
    setIsHideRemoveOpen(true);
  };

  const current = cottages.find(({ id }) => id === cottageId);
  const onlyRemove = current && (!current.reviewed_at || !!current.hidden_at);

  const showFixedLink = cottages.some(({ commission }) => commission === false);

  if (!isStrict && !isCanUse) {
    return (
      <Box p={2}>
        <Typography paragraph>
          <FormattedMessage
            id="cottages.needMoreInfo"
            defaultMessage="We need more information about you before you can create a listing"
          />
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to={`${PATHS.users.profile}?next=${PATHS.cottages.root}`}
        >
          <FormattedMessage
            id="cottages.clickHereToAdd"
            defaultMessage="Click here to add the missing information"
          />
        </Button>
      </Box>
    );
  }

  return (
    <>
      <div className={classes.wrap}>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to={showFixedLink ? PATHS.cottages.createFixed : PATHS.cottages.create}
          className={classes.button}
        >
          <Add />
          <FormattedMessage
            id="cottages.newAd"
            defaultMessage="Create new ad"
          />
        </Button>
        {isLoading && <CircularProgress />}
        {!isLoading && !!notReviewed.length && (
          <div className={classes.inProgress}>
            <Typography className={classes.title} variant="subtitle1">
              <FormattedMessage
                id="Not reviewed"
                defaultMessage="Not reviewed"
              />
            </Typography>
            {notReviewed.map((cottage, i) => (
              <CottagesListItem
                key={cottage.id}
                cottage={cottage}
                defaultExpanded={i === 0}
                title={cottage.title_sv}
                disabledButtons={updatingDialogs.onActivate}
                onActivate={() => onActivateClick(cottage.id)}
                onHideRemoveOpen={() => openHideRemove(cottage.id)}
              />
            ))}
          </div>
        )}
        {!isLoading && !!reviewed.length && (
          <div>
            <Typography className={classes.title} variant="subtitle1">
              <FormattedMessage
                id="Reviewed"
                defaultMessage="Reviewed"
              />
            </Typography>
            {reviewed.map((cottage, i) => (
              <CottagesListItem
                key={cottage.id}
                cottage={cottage}
                defaultExpanded={i === 0 && !notReviewed.length}
                title={cottage.title_sv}
                disabledButtons={updatingDialogs.onActivate}
                onActivate={() => onActivateClick(cottage.id)}
                onHideRemoveOpen={() => openHideRemove(cottage.id)}
              />
            ))}
          </div>
        )}
      </div>
      <Dialog open={isHideRemoveOpen} onClose={closeHideRemove}>
        <HideRemove
          onlyRemove={onlyRemove}
          disabled={updatingDialogs.onActivate || updatingDialogs.onRemove}
          onClose={closeHideRemove}
          onHide={onHide}
          onRemove={onRemoveClick}
        />
      </Dialog>
    </>
  );
};

Cottages.propTypes = {
  cottages: PropTypes.arrayOf(PropTypes.shape({})),
  isCanUse: PropTypes.bool,
  isLoading: PropTypes.bool,
  isStrict: PropTypes.bool,
  updatingDialogs: PropTypes.shape({
    onActivate: PropTypes.bool,
    onRemove: PropTypes.bool,
  }).isRequired,
  onActivate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default Cottages;
